import { PersonData, VehicleData } from "../types";
import { API_URL } from "./api";

type IdentityRole = "buyer" | "seller";

interface VehiclePayload {
  registration_number?: string;
  vin?: string;
  first_registration_date?: string;
  brand?: string;
  type?: string;
  model?: string;
   genre?: string;
  mileage?: string;
}

interface IdentityPayload {
  is_company?: boolean;
  company_name?: string;
  last_name?: string;
  first_name?: string;
  birth_date?: string;
  gender?: string;
  nationality?: string;
  birth_place?: string; // Lieu de naissance issu de /ocr/identity
  document_number?: string;
  role?: string;
  siret?: string;
  address?: string;
  address_line1?: string;
  address_line2?: string;
  street_number?: string;
  street_name?: string;
  address_complement?: string;
  country?: string;
  postal_code?: string;
  city?: string;
}

interface CarteGriseApiResponse {
  vehicle?: VehiclePayload;
  error?: string;
  task_id?: string;
}

interface IdentityApiResponse {
  identity?: IdentityPayload;
  role?: IdentityRole;
  error?: string;
  task_id?: string;
}

const postOcrFile = async <T extends { error?: string }>(
  endpoint: string,
  file: File,
  extraFields: Record<string, string> = {},
): Promise<T> => {
  const formData = new FormData();
  formData.append("file", file);
  Object.entries(extraFields).forEach(([key, value]) => formData.append(key, value));

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 20000);

  try {
    const response = await fetch(`${API_URL}${endpoint}`, {
      method: "POST",
      body: formData,
      signal: controller.signal,
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      throw new Error(`HTTP ${response.status} - ${response.statusText}`);
    }

    const payload = (await response.json()) as T;
    if ((payload as { error?: string }).error) {
      throw new Error((payload as { error?: string }).error || "OCR API error");
    }

    return payload;
  } catch (error) {
    clearTimeout(timeoutId);
    console.error(`[AutoCerfa] OCR request failed on ${endpoint}:`, error);
    throw error;
  }
};

const stripFieldPrefix = (value?: string): string => {
  if (!value) return "";
  const trimmed = value.trim();
  // Retire uniquement les préfixes de type "A.", "B.", "J.1" (avec un point après la lettre)
  const m = trimmed.match(/^[A-Z](?:\.[0-9]+)?\.\s*(.+)$/i);
  if (m && m[1]) return m[1].trim();
  return trimmed;
};

const normalizeRegistration = (value?: string): string => {
  if (!value) return "";
  let v = stripFieldPrefix(value).toUpperCase();
  v = v.replace(/[^A-Z0-9-]/g, "");
  // Format FR récent : AA-123-AA (7 ou 8 chars avec tirets), mais on tolère anciens formats
  if (v.length >= 6 && v.length <= 10) return v;
  return v.slice(0, 10);
};

const normalizeVin = (value?: string): string => {
  if (!value) return "";
  let v = stripFieldPrefix(value).toUpperCase();
  v = v.replace(/[^A-Z0-9]/g, "");
  if (v.length >= 17) return v.slice(0, 17);
  return v;
};

const normalizeBrand = (value?: string): string => {
  if (!value) return "";
  let v = stripFieldPrefix(value).toUpperCase().trim();
  const corrections: Record<string, string> = {
    "EUGEOT": "PEUGEOT",
    "RENAUL": "RENAULT",
    "CITROEN": "CITROEN",
  };
  if (corrections[v]) return corrections[v];
  // Si la valeur est proche de PEUGEOT sans P
  if (v === "EUGEOT") return "PEUGEOT";
  return v;
};

const GENRE_WHITELIST = new Set([
  "VP",
  "CTTE",
  "CAM",
  "VASP",
  "MTT1",
  "MTT2",
  "TRR",
  "DERIV-VP",
  "QUAD",
  "VTSU",
]);

const normalizeGenre = (value?: string): string => {
  if (!value) return "";
  const v = stripFieldPrefix(value).toUpperCase().replace(/\s+/g, "");
  if (GENRE_WHITELIST.has(v)) return v;
  // Certains OCR collent des codes type, on ignore si ça ne ressemble pas à un genre
  return v.length <= 5 ? v : "";
};

const normalizeType = (value?: string): string => {
  if (!value) return "";
  const v = stripFieldPrefix(value).toUpperCase().trim();
  // Si le champ ressemble à un genre (VP, CTTE...), on ne le met pas dans D.2
  if (GENRE_WHITELIST.has(v)) return "";
  return v.replace(/\s+/g, "");
};

const mapVehiclePayload = (vehicle?: VehiclePayload): Partial<VehicleData> => ({
  registrationNumber: normalizeRegistration(vehicle?.registration_number),
  vin: normalizeVin(vehicle?.vin),
  firstRegistrationDate: vehicle?.first_registration_date ?? "",
  brand: normalizeBrand(vehicle?.brand),
  // type = D.2 / genre = J.1 (si genre absent, fallback sur type)
  type: normalizeType(vehicle?.type),
  model: stripFieldPrefix(vehicle?.model),
  genre: normalizeGenre(vehicle?.genre) || normalizeGenre(vehicle?.type),
  mileage: vehicle?.mileage ?? "",
});

const mapIdentityPayload = (identity?: IdentityPayload): Partial<PersonData> => {
  const addressFromStreet = [identity?.street_number, identity?.street_name].filter(Boolean).join(" ").trim();
  const addressFromLines = [identity?.address_line1, identity?.address_line2].filter(Boolean).join(" ").trim();
  const addressRaw = identity?.address || "";
  const fullAddress = [addressFromLines, addressRaw].filter(Boolean).join(" ").trim();
  const normalizedLast = (identity?.last_name || "").replace(/^[A-Z]\.\s*/i, "").trim();
  const normalizedFirst = (identity?.first_name || "").replace(/^[A-Z]\.\s*/i, "").trim();
  const normalizedDoc = (identity?.document_number || "").replace(/^[A-Z]\.\s*/i, "").trim();

  return {
    isCompany: identity?.is_company ?? false,
    companyName: identity?.company_name ?? "",
    lastName: normalizedLast,
    firstName: normalizedFirst,
    birthDate: identity?.birth_date ?? "",
    birthPlace: identity?.birth_place ?? "",
    gender: identity?.gender ?? "",
    nationality: identity?.nationality ?? "",
    documentNumber: normalizedDoc,
    role: identity?.role ?? "",
    siret: identity?.siret ?? "",
    addressLine1: identity?.address_line1 ?? "",
    addressLine2: identity?.address_line2 ?? "",
    streetNumber: identity?.street_number ?? "",
    streetName: identity?.street_name ?? "",
    addressComplement: identity?.address_complement ?? "",
    country: identity?.country ?? "",
    address: fullAddress || addressFromStreet || addressRaw,
    postalCode: identity?.postal_code ?? "",
    city: identity?.city ?? "",
  };
};

export const extractCarteGriseData = async (file: File): Promise<{ vehicle: Partial<VehicleData> }> => {
  const response = await postOcrFile<CarteGriseApiResponse>("/ocr/carte-grise", file);
  return {
    vehicle: mapVehiclePayload(response.vehicle),
    // @ts-expect-error pour compatibilité actuelle, task_id sera consommé côté appelant
    taskId: response.task_id,
  };
};

export const extractIdentityData = async (
  file: File,
  isSeller: boolean = false,
): Promise<{ buyer?: Partial<PersonData>; seller?: Partial<PersonData>; taskId?: string }> => {
  const role: IdentityRole = isSeller ? "seller" : "buyer";
  const response = await postOcrFile<IdentityApiResponse>("/ocr/identity", file, { role });
  const personData = mapIdentityPayload(response.identity);
  const resolvedRole = response.role ?? role;
  const personWithRole = { ...personData, role: resolvedRole };

  return {
    [resolvedRole]: personWithRole,
    taskId: response.task_id,
  };
};
